import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { downloadExcel } from "react-export-table-to-excel";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { BiArrowBack, BiSearch, BiListUl, BiX } from "react-icons/bi";

import { fetchGet } from "../../fetchers";
import { Colors } from "../../assets/Colors";

// Gudang › Fast Moving — barang yang KELUAR (pengeluaran Approved, saat stok
// benar-benar dipotong), dijumlahkan per barang dan diurutkan dari terbanyak.
// Bulan & Tahun masing-masing bisa "Semua". Qty tiap barang dalam satuannya
// sendiri (Liter, Pcs, Kg, …), jadi tinggi batang antarbarang tidak setara.

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli",
  "Agustus", "September", "Oktober", "November", "Desember"];
const TOP_OPTIONS = [
  { value: "all", label: "Semua barang" },
  { value: "20", label: "Top 20" },
  { value: "50", label: "Top 50" },
  { value: "100", label: "Top 100" },
];
const PAGE_SIZE = 50;
const BAR_SLOT = 28; // lebar per barang di grafik (px) — grafik bisa di-scroll ke samping

const fmt = (n) => new Intl.NumberFormat("id-ID", { maximumFractionDigits: 2 }).format(n ?? 0);
const fmtTgl = (s) => (s ? `${s.slice(8, 10)}-${s.slice(5, 7)}-${s.slice(0, 4)}` : "");
const potong = (s, n = 16) => (s && s.length > n ? s.slice(0, n - 1) + "…" : s);

const labelCls = "block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2";
const selectCls = "w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white h-10";

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  return (
    <div className="bg-white border rounded shadow px-3 py-2 text-xs">
      <div className="font-semibold text-gray-800 mb-1">{r.NamaBarang}</div>
      <div className="text-gray-600">{r.BarangID}</div>
      <div className="mt-1">Total keluar: <strong>{fmt(r.TotalKeluar)} {r.NamaSatuan}</strong></div>
      <div>Jumlah transaksi: {r.JumlahTransaksi}</div>
    </div>
  );
}

// Popup daftar pengeluaran satu barang pada periode yang sama dengan tabel.
function DetailModal({ item, bulan, tahun, periodeLabel, onClose }) {
  const mutation = useMutation((q) => fetchGet("Procurement/GoodsExpenditure/fast-moving/detail" + q));
  useEffect(() => {
    mutation.mutate(`?barang=${encodeURIComponent(item.BarangID)}&bulan=${bulan}&tahun=${tahun}`, {
      onError: () => toast.error("Gagal memuat detail pengeluaran"),
    });
  }, [item.BarangID]); // eslint-disable-line
  const rows = mutation.data?.data ?? [];
  const hariBerbeda = new Set(rows.map((r) => r.Tanggal)).size;

  const handleExcel = () => {
    if (rows.length === 0) return;
    downloadExcel({
      fileName: `pengeluaran_${item.BarangID}_${bulan}_${tahun}`,
      sheet: "Pengeluaran",
      tablePayload: {
        header: ["Tanggal", "No. Pengeluaran", "Peminta", "Departemen", "Activity", "Qty Keluar", "Satuan", "Keterangan", "No. MSR"],
        body: rows.map((r) => [fmtTgl(r.Tanggal), r.GoodsExpenditureID, r.NamaPeminta, r.Departemen,
          r.NamaActivity, r.QtyKeluar, item.NamaSatuan, r.Keterangan, r.PurchaseRequisitionID]),
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={onClose}>
      <div className="w-full max-w-5xl max-h-[85vh] flex flex-col bg-white rounded-xl shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-bold text-gray-800">{item.NamaBarang}</h3>
            <p className="text-xs text-gray-500">
              {item.BarangID} · {periodeLabel} · total keluar <strong>{fmt(item.TotalKeluar)} {item.NamaSatuan}</strong>
              {" "}dalam {item.JumlahTransaksi} transaksi{rows.length > 0 && <> pada {hariBerbeda} tanggal berbeda</>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={handleExcel} disabled={rows.length === 0}
              className="h-9 px-3 rounded-lg border text-sm font-medium bg-white disabled:opacity-40"
              style={{ borderColor: Colors.primary, color: Colors.primary }}>
              Download Excel
            </button>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-800" title="Tutup">
              <BiX className="text-2xl" />
            </button>
          </div>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0">
              <tr className="text-left text-xs uppercase text-white" style={{ background: Colors.primary }}>
                <th className="px-3 py-2 w-12">No</th>
                <th className="px-3 py-2">Tanggal</th>
                <th className="px-3 py-2">No. Pengeluaran</th>
                <th className="px-3 py-2">Peminta</th>
                <th className="px-3 py-2">Departemen</th>
                <th className="px-3 py-2">Activity</th>
                <th className="px-3 py-2 text-right">Qty Keluar</th>
                <th className="px-3 py-2">Keterangan</th>
              </tr>
            </thead>
            <tbody>
              {mutation.isLoading ? (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-gray-400">Memuat…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-gray-400">Tidak ada data</td></tr>
              ) : rows.map((r, i) => (
                <tr key={`${r.GoodsExpenditureID}-${i}`} className={i % 2 ? "bg-gray-50" : "bg-white"}>
                  <td className="px-3 py-2 text-gray-500">{i + 1}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-800">{fmtTgl(r.Tanggal)}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-700 whitespace-nowrap">
                    {r.GoodsExpenditureID}
                    {r.PurchaseRequisitionID && <span className="block text-[10px] text-gray-400">{r.PurchaseRequisitionID}</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-700">{r.NamaPeminta}</td>
                  <td className="px-3 py-2 text-gray-700">{r.Departemen}</td>
                  <td className="px-3 py-2 text-gray-600">{r.NamaActivity}</td>
                  <td className="px-3 py-2 text-right font-medium text-gray-800 whitespace-nowrap">{fmt(r.QtyKeluar)} {item.NamaSatuan}</td>
                  <td className="px-3 py-2 text-gray-600">{r.Keterangan}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function FastMovingPage() {
  const navigate = useNavigate();
  const [bulan, setBulan] = useState("all");
  const [tahun, setTahun] = useState(String(new Date().getFullYear()));
  const [top, setTop] = useState("all");
  const [logScale, setLogScale] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [detailItem, setDetailItem] = useState(null);

  const mutation = useMutation((q) => fetchGet("Procurement/GoodsExpenditure/fast-moving" + q));

  useEffect(() => {
    mutation.mutate(`?bulan=${bulan}&tahun=${tahun}`, {
      onError: () => toast.error("Gagal memuat data Fast Moving"),
    });
    setPage(1);
  }, [bulan, tahun]); // eslint-disable-line

  const items = useMemo(() => mutation.data?.data?.Items ?? [], [mutation.data]);
  const years = useMemo(() => mutation.data?.data?.Years ?? [], [mutation.data]);
  // Tahun terpilih tetap ada di opsi walau datanya belum ada (mis. tahun berjalan baru mulai).
  const yearOptions = useMemo(() => {
    const ys = new Set(years.map(String));
    if (tahun !== "all") ys.add(tahun);
    return [...ys].sort((a, b) => b - a);
  }, [years, tahun]);

  const chartData = top === "all" ? items : items.slice(0, parseInt(top, 10));
  const chartWidth = Math.max(900, chartData.length * BAR_SLOT + 80);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) =>
      (r.NamaBarang || "").toLowerCase().includes(q) || (r.BarangID || "").toLowerCase().includes(q));
  }, [items, search]);
  const maxPage = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  // Peringkat mengikuti urutan seluruh data (bukan posisi setelah dicari).
  const rankOf = useMemo(() => new Map(items.map((r, i) => [r.BarangID, i + 1])), [items]);

  const periodeLabel = `${bulan === "all" ? "Semua bulan" : BULAN[parseInt(bulan, 10) - 1]} ${tahun === "all" ? "· Semua tahun" : tahun}`;

  const handleExcel = () => {
    if (filtered.length === 0) { toast.info("Tidak ada data untuk diunduh"); return; }
    downloadExcel({
      fileName: `fast-moving_${bulan}_${tahun}`,
      sheet: "Fast Moving",
      tablePayload: {
        header: ["Peringkat", "Kode Barang", "Nama Barang", "Total Keluar", "Satuan", "Jumlah Transaksi", "Terakhir Keluar"],
        body: filtered.map((r) => [rankOf.get(r.BarangID), r.BarangID, r.NamaBarang, r.TotalKeluar,
          r.NamaSatuan, r.JumlahTransaksi, fmtTgl(r.TerakhirKeluar)]),
      },
    });
  };

  return (
    <div className="md:ml-64">
      <div style={{ background: Colors.primary }} className="pt-14 pb-20 px-8" />
      <div className="px-4 md:px-8 -mt-16 pb-16">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {/* Header + filter */}
          <div className="px-6 py-4 border-b">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => navigate("/gudang")} className="text-gray-500 hover:text-gray-800">
                <BiArrowBack className="text-xl" />
              </button>
              <div>
                <h2 className="text-lg font-bold text-gray-800">Fast Moving</h2>
                <p className="text-xs text-gray-500">
                  Barang yang keluar dari gudang (pengeluaran berstatus Approved), diurutkan dari qty terbanyak.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="w-40">
                <label className={labelCls}>Bulan</label>
                <select value={bulan} onChange={(e) => setBulan(e.target.value)} className={selectCls}>
                  <option value="all">Semua</option>
                  {BULAN.map((b, i) => <option key={b} value={String(i + 1)}>{b}</option>)}
                </select>
              </div>
              <div className="w-36">
                <label className={labelCls}>Tahun</label>
                <select value={tahun} onChange={(e) => setTahun(e.target.value)} className={selectCls}>
                  <option value="all">Semua</option>
                  {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div className="w-40">
                <label className={labelCls}>Grafik</label>
                <select value={top} onChange={(e) => setTop(e.target.value)} className={selectCls}>
                  {TOP_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 h-10 text-sm text-gray-700 cursor-pointer select-none">
                <input type="checkbox" checked={logScale} onChange={(e) => setLogScale(e.target.checked)} />
                Skala logaritmik
              </label>
            </div>
          </div>

          {/* Grafik */}
          <div className="px-6 py-4 border-b">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">
                Qty keluar per barang — {periodeLabel}
              </h3>
              <span className="text-xs text-gray-500">
                {chartData.length} dari {items.length} barang · qty dalam satuan masing-masing barang
              </span>
            </div>
            {mutation.isLoading ? (
              <div className="py-16 text-center text-gray-400 text-sm">Memuat…</div>
            ) : chartData.length === 0 ? (
              <div className="py-16 text-center text-gray-400 text-sm">Tidak ada barang keluar pada periode ini</div>
            ) : (
              <div className="overflow-x-auto">
                <BarChart width={chartWidth} height={400} data={chartData}
                  margin={{ top: 8, right: 16, bottom: 8, left: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="NamaBarang" interval={0} angle={-55} textAnchor="end" height={120}
                    tick={{ fontSize: 10 }} tickFormatter={(v) => potong(v)} />
                  <YAxis scale={logScale ? "log" : "auto"} domain={logScale ? [1, "auto"] : [0, "auto"]}
                    allowDataOverflow tick={{ fontSize: 11 }} tickFormatter={(v) => fmt(v)} width={70} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(241,133,67,0.08)" }} />
                  <Bar dataKey="TotalKeluar" fill={Colors.primary} maxBarSize={22} isAnimationActive={false} />
                </BarChart>
              </div>
            )}
          </div>

          {/* Tabel */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-b bg-gray-50">
            <div className="relative w-full max-w-sm">
              <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
              <input type="text" value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Cari kode atau nama barang…"
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg outline-none bg-white" />
            </div>
            <button onClick={handleExcel}
              className="h-10 px-4 rounded-lg border text-sm font-medium bg-white"
              style={{ borderColor: Colors.primary, color: Colors.primary }}>
              Download Excel
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-white" style={{ background: Colors.primary }}>
                  <th className="px-4 py-3 w-16">No</th>
                  <th className="px-4 py-3">Kode Barang</th>
                  <th className="px-4 py-3">Nama Barang</th>
                  <th className="px-4 py-3 text-right">Total Keluar</th>
                  <th className="px-4 py-3">Satuan</th>
                  <th className="px-4 py-3 text-right">Jumlah Transaksi</th>
                  <th className="px-4 py-3">Terakhir Keluar</th>
                  <th className="px-4 py-3 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                    {mutation.isLoading ? "Memuat…" : "Tidak ada data"}
                  </td></tr>
                ) : pageRows.map((r, i) => (
                  <tr key={r.BarangID} className={i % 2 ? "bg-gray-50" : "bg-white"}>
                    <td className="px-4 py-2 text-gray-500">{rankOf.get(r.BarangID)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-700">{r.BarangID}</td>
                    <td className="px-4 py-2 text-gray-800">{r.NamaBarang}</td>
                    <td className="px-4 py-2 text-right font-medium text-gray-800">{fmt(r.TotalKeluar)}</td>
                    <td className="px-4 py-2 text-gray-600">{r.NamaSatuan}</td>
                    <td className="px-4 py-2 text-right text-gray-700">{r.JumlahTransaksi}</td>
                    <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{fmtTgl(r.TerakhirKeluar)}</td>
                    <td className="px-4 py-2 text-right">
                      <button onClick={() => setDetailItem(r)} title="Lihat daftar pengeluaran barang ini"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 border-gray-300 hover:bg-gray-50">
                        <BiListUl className="text-sm" /> Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {detailItem && (
            <DetailModal item={detailItem} bulan={bulan} tahun={tahun} periodeLabel={periodeLabel}
              onClose={() => setDetailItem(null)} />
          )}

          <div className="flex items-center justify-between px-6 py-3 border-t bg-gray-50 text-xs text-gray-600">
            <span>{filtered.length} barang{search ? ` (dari ${items.length})` : ""}</span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 rounded border bg-white disabled:opacity-40">Prev</button>
              <span>Halaman {page} / {maxPage}</span>
              <button disabled={page >= maxPage} onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 rounded border bg-white disabled:opacity-40">Next</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
